import { z } from 'zod';
import { jsonObjectSchema } from './json.ts';
import {
  sceneThemeSchema,
  sceneViewportSchema,
  visualIntentSchema,
} from './scene.ts';

export const sceneCollectionSchema = z.enum([
  'dataViews',
  'definitions',
  'instances',
  'nodes',
  'repeats',
  'layouts',
  'relations',
  'states',
  'timelines',
  'interactions',
  'constructs',
  'cameras',
  'landmarks',
]);

const sceneCommandCoreSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('scene.intent.set'),
    value: visualIntentSchema.nullable(),
  }).strict(),
  z.object({
    type: z.literal('scene.viewport.patch'),
    value: sceneViewportSchema.partial(),
  }).strict(),
  z.object({
    type: z.literal('scene.theme.patch'),
    value: sceneThemeSchema.partial(),
  }).strict(),
  z.object({
    type: z.literal('scene.metadata.patch'),
    value: jsonObjectSchema,
  }).strict(),
  z.object({
    type: z.literal('scene.item.upsert'),
    collection: sceneCollectionSchema,
    value: jsonObjectSchema,
  }).strict(),
  z.object({
    type: z.literal('scene.item.patch'),
    collection: sceneCollectionSchema,
    id: z.string().min(1),
    value: jsonObjectSchema,
  }).strict(),
  z.object({
    type: z.literal('scene.item.remove'),
    collection: sceneCollectionSchema,
    id: z.string().min(1),
  }).strict(),
  z.object({
    type: z.literal('scene.item.move'),
    collection: sceneCollectionSchema,
    id: z.string().min(1),
    index: z.number().int().nonnegative(),
  }).strict(),
  z.object({
    type: z.literal('scene.collection.reorder'),
    collection: sceneCollectionSchema,
    ids: z.array(z.string().min(1)).max(50000),
  }).strict(),
]);

export const sceneCommandSchema = z.union([
  sceneCommandCoreSchema,
  z.object({
    type: z.literal('scene.batch'),
    commands: z.array(sceneCommandCoreSchema).min(1).max(1000),
  }).strict(),
]);

export type SceneCollection = z.infer<typeof sceneCollectionSchema>;
export type SceneCommand = z.infer<typeof sceneCommandSchema>;
